import React from 'react';
import { Icon, Spin, Modal } from '../components/ui.jsx';
import { api, showToast } from '../api.js';

// Metadata per ogni setting: etichetta user-friendly, placeholder, gruppo
const META = {
  // Azienda
  company_name:      {group:'company',  label:'Nome azienda',         ph:'Es. EjaTV / TDP Srl'},
  admin_email:       {group:'company',  label:'Email amministratore', ph:'admin@ejatv.com'},
  admin_password:    {group:'company',  label:'Password admin',       ph:'••••••••'},
  // SMTP
  smtp_host:         {group:'smtp',     label:'Server SMTP',          ph:'smtp.gmail.com'},
  smtp_port:         {group:'smtp',     label:'Porta',                ph:'587'},
  smtp_user:         {group:'smtp',     label:'Username',             ph:'tuo@gmail.com'},
  smtp_password:     {group:'smtp',     label:'Password / App Pass',  ph:'••••••••'},
  smtp_from_name:    {group:'smtp',     label:'Nome mittente',        ph:'EjaTV Broadcast'},
  // AI
  anthropic_api_key: {group:'ai',       label:'Anthropic API Key',    ph:'sk-ant-...'},
  // SCAN360 / FiC
  scan360_api_url:           {group:'fic',  label:'URL API SCAN360',      ph:'https://api.scan360.app'},
  scan360_api_token:         {group:'fic',  label:'Service token',        ph:'sct_...'},
  scan360_company_id:        {group:'fic',  label:'Company ID',           ph:'UUID azienda in SCAN360'},
  scan360_fic_enabled:       {group:'fic',  label:'Integrazione attiva',  ph:'true / false'},
  scan360_sync_interval_min: {group:'fic',  label:'Sync interval (min)',  ph:'30'},
  // WhatsApp
  wa_enabled:        {group:'wa',       label:'Notifiche attive',     ph:'true / false'},
  wa_service_url:    {group:'wa',       label:'URL servizio WA',      ph:'http://ejatv-wa:3100'},
  wa_sender_name:    {group:'wa',       label:'Nome mittente',        ph:'EjaTV Broadcast'},
};

// Card generica per un singolo setting
const SettingRow = ({s, editing, setEditing, saving, save}) => {
  const meta = META[s.key] || {label:s.key, ph:''};
  const isEditing = editing[s.key] !== undefined;
  const isPassword = s.type === 'password';
  return (
    <div style={{padding:'10px 0',borderBottom:'1px solid var(--n100)'}}>
      <div style={{fontSize:11,fontWeight:600,color:'var(--n500)',
        textTransform:'uppercase',letterSpacing:'.04em',marginBottom:4}}>
        {meta.label}
      </div>
      {isEditing ? (
        <div style={{display:'flex',gap:6}}>
          <input className="input"
            type={isPassword ? 'password' : 'text'}
            value={editing[s.key]}
            onChange={e => setEditing(p => ({...p, [s.key]: e.target.value}))}
            onKeyDown={e => e.key === 'Enter' && save(s.key)}
            placeholder={meta.ph}
            autoFocus
            style={{flex:1,fontSize:13}}/>
          <button className="btn btn-primary btn-sm" onClick={() => save(s.key)}
            disabled={saving[s.key]} style={{padding:'4px 8px'}}>
            {saving[s.key] ? <Spin/> : <Icon name="check" size={13} color="#fff"/>}
          </button>
          <button className="btn btn-ghost btn-sm" style={{padding:'4px 8px'}}
            onClick={() => setEditing(p => { const n={...p}; delete n[s.key]; return n; })}>
            <Icon name="x" size={13} color="var(--n500)"/>
          </button>
        </div>
      ) : (
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {isPassword ? (
            <span style={{fontSize:13,color:s.has_value?'var(--n700)':'var(--n300)',
              fontFamily:'ui-monospace, monospace',flex:1}}>
              {s.has_value ? (s.value_masked || '••••••••') : 'Non configurata'}
            </span>
          ) : (
            <span style={{fontSize:13,color:s.has_value?'var(--n700)':'var(--n300)',flex:1,
              overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {s.value || 'Non impostato'}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" style={{padding:'3px 8px',fontSize:11,flexShrink:0}}
            onClick={() => setEditing(p => ({...p, [s.key]: ''}))}>
            <Icon name="pencil" size={11} color="var(--n400)"/>
          </button>
        </div>
      )}
    </div>
  );
};

// Card di gruppo
const GroupCard = ({icon, iconBg, iconColor, title, subtitle, badge, footer, children}) => (
  <div className="card" style={{padding:0,marginBottom:16,overflow:'hidden'}}>
    <div style={{padding:'14px 18px',background:iconBg||'var(--n50)',
      borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
      <div style={{width:34,height:34,borderRadius:10,
        background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
        boxShadow:'0 1px 2px rgba(0,0,0,.05)'}}>
        <Icon name={icon} size={16} color={iconColor||'var(--n600)'}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:700,fontSize:14,color:'var(--n800)'}}>{title}</div>
        {subtitle && <div style={{fontSize:11,color:'var(--n500)',marginTop:1}}>{subtitle}</div>}
      </div>
      {badge}
    </div>
    <div style={{padding:'6px 18px 14px'}}>
      {children}
    </div>
    {footer && (
      <div style={{padding:'10px 18px',borderTop:'1px solid var(--n100)',background:'var(--n50)'}}>
        {footer}
      </div>
    )}
  </div>
);

export const SettingsPage = () => {
  const [settings, setSettings] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState({});
  const [saving, setSaving] = React.useState({});
  const [testing, setTesting] = React.useState(false);

  // FiC state
  const [ficStatus, setFicStatus] = React.useState(null);
  const [ficSyncing, setFicSyncing] = React.useState(false);
  const [ficTesting, setFicTesting] = React.useState(false);

  // WhatsApp state
  const [waStatus, setWaStatus] = React.useState(null);
  const [waQrOpen, setWaQrOpen] = React.useState(false);
  const [waQr, setWaQr] = React.useState(null);

  const load = () => api('/settings').then(d => { setSettings(d); setLoading(false); }).catch(() => setLoading(false));
  React.useEffect(() => { load(); }, []);

  const save = async (key) => {
    const value = editing[key];
    if (value === undefined) return;
    setSaving(p => ({...p, [key]: true}));
    try {
      await fetch('/api/settings/' + key, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({value})
      });
      setEditing(p => { const n = {...p}; delete n[key]; return n; });
      load();
      showToast('Salvato', 'success');
    } catch(e) { showToast('Errore: ' + e.message, 'error'); }
    finally { setSaving(p => ({...p, [key]: false})); }
  };

  const testAnthropic = async () => {
    setTesting(true);
    try { const r = await api('/settings/test/api-key'); showToast(r.message || 'OK', r.status==='ok'?'success':'error'); }
    catch(e) { showToast('Errore: ' + e.message, 'error'); }
    finally { setTesting(false); }
  };

  const testSmtp = async () => {
    try { const r = await api('/settings/test/smtp','POST'); showToast(r.message || 'Inviato', 'success'); }
    catch(e) { showToast('Errore: ' + e.message, 'error'); }
  };

  // FiC handlers
  const testFic = async () => {
    setFicTesting(true);
    try {
      const r = await api('/integrations/scan360/status');
      setFicStatus(r);
      showToast(r.ok ? 'Connessione OK' : 'Connessione fallita', r.ok ? 'success' : 'error');
    } catch(e) { showToast('Errore: ' + e.message, 'error'); }
    finally { setFicTesting(false); }
  };
  const syncFicQuotes = async () => {
    setFicSyncing(true);
    try {
      const r = await api('/integrations/scan360/sync-quotes', 'POST');
      const l = r.local || {};
      showToast(`Sync OK: ${l.new||0} nuovi, ${l.updated||0} aggiornati`, 'success');
      setFicStatus({ok: true, sync: r});
    } catch(e) {}
    finally { setFicSyncing(false); }
  };
  React.useEffect(() => { testFic(); }, []);

  // WA handlers
  const loadWaStatus = async () => {
    try { setWaStatus(await api('/wa/status')); }
    catch(e) { setWaStatus({available: false, error: e.message}); }
  };
  React.useEffect(() => { loadWaStatus(); }, []);

  const openWaPairing = async () => {
    setWaQrOpen(true);
    try { setWaQr(await api('/wa/qr')); } catch(e) {}
  };
  React.useEffect(() => {
    if (!waQrOpen) return;
    const poll = setInterval(async () => {
      try {
        const [qr, st] = await Promise.all([api('/wa/qr'), api('/wa/status')]);
        setWaQr(qr); setWaStatus(st);
        if (st.connected) { setWaQrOpen(false); showToast('WhatsApp connesso', 'success'); }
      } catch(e) {}
    }, 2000);
    return () => clearInterval(poll);
  }, [waQrOpen]);

  const waDisconnect = async () => {
    if (!confirm('Disconnettere WhatsApp? Serverà ri-scansionare il QR per riconnettere.')) return;
    try {
      await api('/wa/disconnect', 'POST');
      showToast('WhatsApp disconnesso', 'success');
      setWaQr(null); loadWaStatus();
    } catch(e) { showToast('Errore: ' + e.message, 'error'); }
  };

  // Raggruppo settings per group
  const byGroup = React.useMemo(() => {
    const g = {};
    for (const s of settings) {
      if (s.type === 'hidden') continue;
      const meta = META[s.key];
      if (!meta) continue;
      (g[meta.group] = g[meta.group] || []).push(s);
    }
    // Ordine stabile: quello definito in META
    const keyOrder = Object.keys(META);
    for (const k in g) g[k].sort((a, b) => keyOrder.indexOf(a.key) - keyOrder.indexOf(b.key));
    return g;
  }, [settings]);

  const rowProps = {editing, setEditing, saving, save};

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Impostazioni</div>
        </div>
        <div className="empty"><Spin/> Caricamento...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Impostazioni</div>
          <div className="page-title-sub">Configurazione del sistema e integrazioni</div>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,alignItems:'start',
        '@media (max-width: 960px)':{gridTemplateColumns:'1fr'}}}
        className="settings-grid">

        {/* ══════════════════════════════════════════════════════════ */}
        {/* COLONNA SINISTRA — Sistema base                            */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div>
          {/* Azienda */}
          <GroupCard icon="building" iconBg="#EEF2FF" iconColor="#4F46E5"
            title="Azienda" subtitle="Dati principali e credenziali amministratore">
            {(byGroup.company || []).map(s => <SettingRow key={s.key} s={s} {...rowProps}/>)}
          </GroupCard>

          {/* Email SMTP */}
          <GroupCard icon="mail" iconBg="#FEF3C7" iconColor="#D97706"
            title="Email / SMTP" subtitle="Invio email di notifica ai tecnici"
            footer={
              <button className="btn btn-ghost btn-sm" onClick={testSmtp}>
                <Icon name="zap" size={13} color="var(--n500)"/>Test invio email
              </button>
            }>
            {(byGroup.smtp || []).map(s => <SettingRow key={s.key} s={s} {...rowProps}/>)}
          </GroupCard>

          {/* AI */}
          <GroupCard icon="sparkles" iconBg="#F3E8FF" iconColor="#7C3AED"
            title="Intelligenza Artificiale" subtitle="Claude per lettere e assistente"
            footer={
              <button className="btn btn-ghost btn-sm" onClick={testAnthropic} disabled={testing}>
                {testing ? <Spin/> : <Icon name="zap" size={13} color="var(--n500)"/>}
                Test API key
              </button>
            }>
            {(byGroup.ai || []).map(s => <SettingRow key={s.key} s={s} {...rowProps}/>)}
          </GroupCard>
        </div>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* COLONNA DESTRA — Integrazioni                              */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div>
          {/* Fatture in Cloud */}
          <GroupCard icon="receipt" iconBg="#DBEAFE" iconColor="#1D4ED8"
            title="Fatture in Cloud"
            subtitle="Preventivi e fatturazione via SCAN360"
            badge={ficStatus && (
              <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,
                background: ficStatus.ok ? '#D1FAE5' : '#FEE2E2',
                color:      ficStatus.ok ? '#065F46' : '#991B1B'}}>
                {ficStatus.ok ? '✓ Connesso' : '✗ Disconnesso'}
              </span>
            )}
            footer={
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                <button className="btn btn-ghost btn-sm" onClick={testFic} disabled={ficTesting}>
                  {ficTesting ? <Spin/> : <Icon name="activity" size={13} color="var(--n500)"/>}
                  Test
                </button>
                <button className="btn btn-primary btn-sm" onClick={syncFicQuotes}
                  disabled={ficSyncing || !ficStatus?.ok}>
                  {ficSyncing ? <Spin/> : <Icon name="download-cloud" size={13} color="#fff"/>}
                  Sincronizza preventivi
                </button>
              </div>
            }>
            {ficStatus?.error && (
              <div style={{padding:'8px 12px',background:'#FEE2E2',color:'#991B1B',
                borderRadius:6,fontSize:11,marginBottom:10}}>
                {typeof ficStatus.error === 'string' ? ficStatus.error : JSON.stringify(ficStatus.error)}
              </div>
            )}
            {(byGroup.fic || []).map(s => <SettingRow key={s.key} s={s} {...rowProps}/>)}
          </GroupCard>

          {/* WhatsApp */}
          <GroupCard icon="message-circle" iconBg="#ECFDF5" iconColor="#059669"
            title="WhatsApp"
            subtitle="Notifiche push ai tecnici in parallelo alle email"
            badge={waStatus && (
              <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600,
                background: waStatus.connected ? '#D1FAE5' : waStatus.available ? '#FEF3C7' : '#FEE2E2',
                color:      waStatus.connected ? '#065F46' : waStatus.available ? '#92400E' : '#991B1B'}}>
                {waStatus.connected ? ('✓ ' + (waStatus.phone || 'Connesso'))
                  : waStatus.available ? 'Non connesso'
                  : 'Offline'}
              </span>
            )}
            footer={
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {!waStatus?.connected && (
                  <button className="btn btn-primary btn-sm" onClick={openWaPairing}
                    disabled={!waStatus?.available}>
                    <Icon name="qr-code" size={13} color="#fff"/>
                    Connetti con QR
                  </button>
                )}
                {waStatus?.connected && (
                  <button className="btn btn-ghost btn-sm" onClick={waDisconnect}>
                    <Icon name="log-out" size={13} color="var(--n500)"/>Disconnetti
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={loadWaStatus}>
                  <Icon name="refresh-cw" size={13} color="var(--n500)"/>Aggiorna
                </button>
              </div>
            }>
            <div style={{fontSize:11,color:'var(--n500)',padding:'6px 0 8px',lineHeight:1.5}}>
              Trigger automatici: ingaggio crew, cambio date, annullamento, promemoria 24h.
              I tecnici disattivano dalla propria scheda.
            </div>
            {(byGroup.wa || []).map(s => <SettingRow key={s.key} s={s} {...rowProps}/>)}
          </GroupCard>
        </div>
      </div>

      {/* Modal QR Pairing WhatsApp */}
      {waQrOpen && (
        <Modal title="Connetti WhatsApp" onClose={() => setWaQrOpen(false)}>
          <div style={{textAlign:'center',padding:'10px 0'}}>
            <div style={{fontSize:13,color:'var(--n600)',marginBottom:20,lineHeight:1.5}}>
              Apri <strong>WhatsApp</strong> sul telefono → Menu (⋮) → <strong>Dispositivi collegati</strong> →
              <strong> Collega un dispositivo</strong> → inquadra il QR.
            </div>

            {waQr?.qr ? (
              <div>
                <img src={waQr.qr} alt="QR WhatsApp" style={{width:280,height:280,margin:'0 auto',
                  border:'1px solid var(--n200)',borderRadius:10,padding:8,background:'#fff'}}/>
                <div style={{marginTop:12,fontSize:12,color:'var(--n500)'}}>
                  QR #{waQr.attempt || 1} — scade tra {waQr.expires_in || 0}s
                </div>
              </div>
            ) : waQr?.expired ? (
              <div style={{padding:40,color:'var(--n400)'}}>
                <Spin/> Generazione nuovo QR...
              </div>
            ) : (
              <div style={{padding:40,color:'var(--n400)'}}>
                <Spin/> {waQr?.message || 'Avvio servizio...'}
              </div>
            )}

            <div style={{marginTop:16,padding:'10px 14px',background:'var(--n50)',
              borderRadius:8,fontSize:11,color:'var(--n500)',textAlign:'left'}}>
              <Icon name="info" size={11} color="var(--n400)"/>{' '}
              Il numero che scansioni diventa il mittente delle notifiche.
              I tecnici lo vedranno nei loro WhatsApp.
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
